import { CallStateEnum } from '@models/call-state.model.ts';
import { SnackbarType } from '@models/app-snackbar.model.ts';
import useSnackbarStore from '@store/snackbar-store/snackbar.store.ts';
import axios from 'axios';
import {
  createEpisode,
  createSeason,
  deleteEpisode,
  deleteEpisodeMany,
  deleteSeason,
  editEpisodeRow,
  editSeason,
  getEpisodes,
  getSeasons
} from '@api/entertainment-api/series-api.ts';
import { SeriesActionParams } from '@modules/entertainment/pages/series/store/series.model.ts';
import { LanguageEnum } from '@models/language.model.ts';
import useLanguageStore from '@store/language-store/language.store.tsx';

const { showSnackbar } = useSnackbarStore.getState();

export const episodeSlice = (set: any, getState: any) => ({
  getEpisodes: async (params: {
    filters: { season: string[] };
    page: number;
    limit: number;
  }) => {
    set({ callState: CallStateEnum.Loading });
    try {
      const response = await getEpisodes({
        ...params,
        sort: 'createdAt,desc'
      });

      const totalPages = Math.ceil(response.total / response.limit);

      set({
        episodesData: response.rows,
        episodesPagination: {
          page: response.page,
          rowsPerPage: response.limit,
          totalPages
        },
        callState: CallStateEnum.Loaded
      });

      return response;
    } catch (error) {
      set({ callState: CallStateEnum.Error });
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message;
        showSnackbar(message, SnackbarType.Error);
      }
    }
  },

  getSeasonsList: async (id: string) => {
    try {
      const seasonData = await getSeasons(id);

      set({ rawSeasons: seasonData });

      const { languageList } = useLanguageStore.getState();
      const defaultLanguage = languageList[0]?.value || LanguageEnum.English;

      getState().setSeasonsListByLanguage(defaultLanguage);
    } catch (error) {
      set({ callState: CallStateEnum.Error });
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message;
        showSnackbar(message, SnackbarType.Error);
      }
    }
  },

  setSeasonsListByLanguage: (language: LanguageEnum) => {
    const rawSeasons = getState().rawSeasons || [];

    const seasonsList = rawSeasons.map((item: any) => {
      const title =
        item.title[language] || item.title[LanguageEnum.English] || 'N/A';

      return {
        title: item.title,
        label: `${title} ${item.seasonNumber || 'N/A'}`,
        value: item._id,
        seasonNumber: item.seasonNumber,
        id: item._id,
        showDeleteIcon: true,
        showEditIcon: true
      };
    });

    set({ seasonsList });
  },

  createSeason: async ({ data, id, handleClose }: SeriesActionParams) => {
    set({ seasonCallState: CallStateEnum.Loading });
    try {
      await createSeason(data);
      await getState().getSeasonsList(id);
      set({ seasonCallState: CallStateEnum.Loaded });
      handleClose?.();
      showSnackbar('snackbar_success_season_text', SnackbarType.Success);
    } catch (error) {
      set({ seasonCallState: CallStateEnum.Error });
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message;
        showSnackbar(message, SnackbarType.Error);
      }
    }
  },

  editSeason: async ({
    data,
    id,
    seriesId,
    handleClose
  }: SeriesActionParams) => {
    set({ seasonCallState: CallStateEnum.Loading });
    try {
      await editSeason(data, id);
      await getState().getSeasonsList(seriesId);
      set({ seasonCallState: CallStateEnum.Loaded });
      handleClose?.();
      showSnackbar('snackbar_update_season_text', SnackbarType.Success);
    } catch (error) {
      set({ seasonCallState: CallStateEnum.Error });
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message;
        showSnackbar(message, SnackbarType.Error);
      }
    }
  },

  deleteSeason: async ({ id, seriesId, onSuccess }: SeriesActionParams) => {
    set({ seasonCallState: CallStateEnum.Loading });
    try {
      await deleteSeason(id);
      await getState().getSeasonsList(seriesId);
      set({ seasonCallState: CallStateEnum.Loaded });
      onSuccess?.();
      showSnackbar('snackbar_delete_season_text', SnackbarType.Success);
    } catch (error) {
      set({ seasonCallState: CallStateEnum.Error });
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message;
        showSnackbar(message, SnackbarType.Error);
      }
    }
  },

  createEpisode: async ({
    data,
    seasonId,
    page = 1,
    limit = 5,
    handleClose
  }: any) => {
    set({ episodeCallState: CallStateEnum.Loading });
    try {
      await createEpisode(data);
      showSnackbar('Episode added successfully', SnackbarType.Success);
      set({ episodeCallState: CallStateEnum.Loaded });
      handleClose?.();

      await getState().getEpisodes({
        filters: { season: [seasonId] },
        page,
        limit
      });
    } catch (error) {
      set({ episodeCallState: CallStateEnum.Error });
      if (axios.isAxiosError(error)) {
        showSnackbar(
          error.response?.data?.message || 'Error',
          SnackbarType.Error
        );
      }
    }
  },

  editEpisode: async ({
    data,
    id,
    seasonId,
    handleClose,
    page = 1,
    limit = 5
  }: any) => {
    set({ episodeCallState: CallStateEnum.Loading });
    try {
      await editEpisodeRow(data, id);
      showSnackbar('Episode updated successfully', SnackbarType.Success);
      set({ episodeCallState: CallStateEnum.Loaded });
      handleClose?.();
      await getState().getEpisodes({
        filters: { season: [seasonId] },
        page,
        limit
      });
    } catch (error) {
      set({ episodeCallState: CallStateEnum.Error });
      if (axios.isAxiosError(error)) {
        showSnackbar(
          error.response?.data?.message || 'Error',
          SnackbarType.Error
        );
      }
    }
  },

  deleteEpisode: async ({
    id,
    seasonId,
    handleClose,
    page = 1,
    limit = 5
  }: any) => {
    set({ episodeCallState: CallStateEnum.Loading });
    try {
      if (id instanceof Array) {
        await deleteEpisodeMany(id);
      } else {
        await deleteEpisode(id as any);
      }
      showSnackbar('Episode deleted successfully', SnackbarType.Success);
      set({ episodeCallState: CallStateEnum.Loaded });
      handleClose?.();
      const res = await getState().getEpisodes({
        filters: { season: [seasonId] },
        page,
        limit
      });

      if (res.rows.length === 0 && page > 1) {
        await getState().getEpisodes({
          filters: { season: [seasonId] },
          page: page - 1,
          limit
        });
      }
    } catch (error) {
      set({ episodeCallState: CallStateEnum.Error });
      if (axios.isAxiosError(error)) {
        showSnackbar(
          error.response?.data?.message || 'Error',
          SnackbarType.Error
        );
      }
    }
  },

  resetSeasonsList: () => {
    set({ seasonsList: [], rawSeasons: [] });
  },
  resetEpisodesList: () => {
    set({
      episodesData: [],
      episodesPagination: {
        page: 1,
        rowsPerPage: 5,
        totalPages: 0
      }
    });
  }
});
